"use client"

/* eslint-disable react-refresh/only-export-components */
import type { IconButtonProps, SpanProps } from "@chakra-ui/react"
import { ClientOnly, IconButton, Skeleton, Span, SegmentGroup } from "@chakra-ui/react"
import { ThemeProvider, useTheme } from "next-themes"
import type { ThemeProviderProps } from "next-themes"
import * as React from "react"
import { LuMoon, LuSun } from "react-icons/lu"
import { useIntl } from 'react-intl'

export type ColorModeProviderProps = ThemeProviderProps

export function ColorModeProvider({ children, ...props }: ColorModeProviderProps) {
  return (
    <ThemeProvider attribute="class" disableTransitionOnChange enableSystem {...props}>
      {children}
      <ThemeColorMetaSync />
    </ThemeProvider>
  )
}

/**
 * `bg.canvas`'s light/dark values — kept in sync with the mobile browser chrome color.
 *
 * The canvas, not `bg.panel`: the browser paints the status bar and the strip
 * behind its own toolbar in this color, and both sit flush against the shell,
 * whose surface is the canvas. Painting them in the panel color left a lighter
 * band above the subheader and made the strip under the tab bar read as part of
 * the bar — the bar looked like it had 90-odd pixels of padding under it.
 */
const BROWSER_CHROME_COLOR = { light: '#EFE9DF', dark: '#1F1F1F' } as const

/**
 * Keeps the `theme-color` meta tag in step with the app's actual color mode, since
 * that mode can be toggled manually and diverge from `prefers-color-scheme` — a
 * static or media-query-only meta tag would then color the browser chrome wrong.
 */
function ThemeColorMetaSync() {
  const { colorMode } = useColorMode()

  React.useEffect(() => {
    if (!colorMode) return
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BROWSER_CHROME_COLOR[colorMode])
  }, [colorMode])

  return null
}

export type ColorMode = "light" | "dark"

export interface UseColorModeReturn {
  colorMode: ColorMode
  setColorMode: (colorMode: ColorMode) => void
  toggleColorMode: () => void
}

export function useColorMode(): UseColorModeReturn {
  const { resolvedTheme, setTheme, forcedTheme } = useTheme() ?? {}
  const colorMode = forcedTheme || resolvedTheme
  const toggleColorMode = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark")
  }
  return {
    colorMode: colorMode as ColorMode,
    setColorMode: setTheme,
    toggleColorMode,
  }
}

export function useColorModeValue<T>(light: T, dark: T) {
  const { colorMode } = useColorMode()
  return colorMode === "dark" ? dark : light
}

export function ColorModeIcon() {
  const { colorMode } = useColorMode()
  return colorMode === "dark" ? <LuMoon /> : <LuSun />
}

export const ColorModeSelector = React.forwardRef<HTMLDivElement>(
  function ColorModeSelector(_, ref) {
    const { theme, setTheme } = useTheme()
    const intl = useIntl()

    return (
      <ClientOnly fallback={<Skeleton h="8" w="24" />}>
        <SegmentGroup.Root
          size="xs"
          value={theme}
          ref={ref}
        >
          <SegmentGroup.Indicator />
          <SegmentGroup.Item value="light" aria-label={intl.formatMessage({ id: 'f499c95a-0a95-487f-a8ac-47f9d77886c5', defaultMessage: 'Light mode' })} onClick={() => setTheme("light")}>
            <LuSun />
          </SegmentGroup.Item>
          <SegmentGroup.Item value="dark" aria-label={intl.formatMessage({ id: 'cfe569bb-b45b-4df2-a5b2-ea75bd25200b', defaultMessage: 'Dark mode' })} onClick={() => setTheme("dark")} >
            <LuMoon />
          </SegmentGroup.Item>
        </SegmentGroup.Root>
      </ClientOnly>
    )
  }
)

export const ColorModeButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  function ColorModeButton(props, ref) {
    const { toggleColorMode } = useColorMode()
    const intl = useIntl()

    return (
      <ClientOnly fallback={<Skeleton boxSize="8" />}>
        <IconButton
          onClick={toggleColorMode}
          variant="ghost"
          size="sm"
          aria-label={intl.formatMessage({
            id: '4a2c9e6f-1d3b-4a8f-9c5e-7b6d2f0a8e13',
            defaultMessage: 'Toggle color mode',
          })}
          ref={ref}
          {...props}
        >
          <ColorModeIcon />
        </IconButton>
      </ClientOnly>
    )
  },
)

export const LightMode = React.forwardRef<HTMLSpanElement, SpanProps>(
  function LightMode(props, ref) {
    return (
      <Span
        color="fg"
        display="contents"
        className="chakra-theme light"

        colorScheme="light"
        ref={ref}
        {...props}
      />
    )
  },
)

export const DarkMode = React.forwardRef<HTMLSpanElement, SpanProps>(
  function DarkMode(props, ref) {
    return (
      <Span
        color="fg"
        display="contents"
        className="chakra-theme dark"

        colorScheme="dark"
        ref={ref}
        {...props}
      />
    )
  },
)
